import { useContext, useState, useRef } from "react";
import Terms from "./AddListingsFormComponents/TermsAndConditions";
import WorkspaceType from "./AddListingsFormComponents/WorkspaceType";
import WorkspaceSpaceSizeRoom from "./AddListingsFormComponents/WorkspaceSpaceSizeRoom";
import WorkspaceAmenities from "./AddListingsFormComponents/WorkspaceAmenities";
import WorkspaceImages from "./AddListingsFormComponents/WorkspaceImages";
import WorkspaceTitleAndDescription from "./AddListingsFormComponents/WorkspaceTitleAndDescription";
import WorkspaceAvailabilities from "./AddListingsFormComponents/WorkspaceAvailabilities";
import WorkspaceSummary from "./AddListingsFormComponents/WorkspaceSummary";
import type { FormData } from "./AddListingsFormComponents/FormData";
import WorkplaceLocation from "./AddListingsFormComponents/WorkspaceLocation";
import WorkspaceBookingDetails from "./AddListingsFormComponents/WorkspaceBookingDetails";
import WorkspaceCommercialDetails from "./AddListingsFormComponents/WorkspaceCommercialDetails";
import axios from "axios";
import { UserContext } from "../../components/UserContext";
import Cookies from "js-cookie";
import { useJsApiLoader } from "@react-google-maps/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const libraries: ("places" | "geocoding" | "marker")[] = [
  "places",
  "geocoding",
  "marker",
];

const initialFormData: FormData = {
  userId: null,
  title: "",
  address: "",
  description: "",
  images: [],
  capacity: 0,
  extraInfo: "",
  facilities: "",
  pricePerHour: 0,
  pricePerDay: 0,
  pricePerWeek: 0,
  pricePerMonth: 0,
  pricePerYear: 0,
  acceptedTerms: false,
  workspaceTypeId: 0, // or whatever default number you want
  desks: 0,
  chairs: 0,
  meetingRooms: 0,
  sizeSqm: 0,
  isFurnished: false,
  rooms: 0,
  workspaceAmenities: [],
  unavailableDates: [],
  city: "",
  state: "",
  postalCode: "",
  country: "",
  isActive: true,
  isInstantBookable: false,
  advancedNoticeHours: 0,
  minimumBookingTime: 0,
  minimumBookingType: 0,
  cancellationPolicy: 0, // 0: Flexible, 1: Moderate, 2: Strict
  // Commercial Real Estate
  ceilingHeight: 0,
  floorLoadCapacity: 0,
  zoningType: "",
  buildingClass: "",
  yearBuilt: 0,
  renovationYear: 0,
  securityDeposit: 0,
  includedUtilities: [],
  operatingExpenses: 0,
  taxAssessment: 0,
  commonAreaMaintenance: 0,
  // Industrial / Warehouse
  loadingDockCount: 0,
  dockHeight: "",
  driveInDoors: false,
  overheadCranes: false,
  truckManeuveringSpace: "",
  sprinklerSystem: false,
  // Parking
  parkingSpotsTotal: 0,
  parkingSpotsReserved: 0,
  parkingFee: 0,
  electricVehicleCharging: 0,
  publicTransportProximity: "",
  // Tenant Improvement
  tenantImprovementAllowance: 0,
  buildOutStatus: "",
  landlordWorkLetter: false,
};

interface AddListingsFormProps {
  onBack?: () => void;
  onSuccess?: () => void;
}

export default function AddListingsForm({
  onBack,
  onSuccess,
}: AddListingsFormProps) {
  const totalPages = 11;
  const { user } = useContext(UserContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Load Google Maps API
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
    version: "weekly",
  });

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const nextPage = () => {
    if (currentPage === 1 && !formData.acceptedTerms) {
      toast.warning("You must accept the Terms & Conditions to continue");
      return;
    }
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      scrollToTop();
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      scrollToTop();
    }
  };

  const handleFormSubmit = async () => {
    try {
      const token = Cookies.get("authToken"); // Retrieve the token from cookies
      formData.userId = user?.id || null; // Ensure userId is set
      const response = await axios.post("/api/workspace/create", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        setFormData(initialFormData);
        onSuccess?.(); // Call the success callback to close the form
        // Don't navigate here since AccountPage will handle showing the correct page
        navigate(`/workspace/${response.data.workspaceId}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const progressPercentage = ((currentPage - 1) / (totalPages - 1)) * 100;

  const renderFormPage = () => {
    switch (currentPage) {
      case 1:
        return (
          <Terms
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 2:
        if (!isGoogleMapsLoaded) {
          return (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
                <p className="text-gray-600">Loading Google Maps...</p>
              </div>
            </div>
          );
        }
        return (
          <WorkplaceLocation
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 3:
        return (
          <WorkspaceType
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 4:
        return (
          <WorkspaceSpaceSizeRoom
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 5:
        return (
          <WorkspaceCommercialDetails
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 6:
        return (
          <WorkspaceAmenities
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 7:
        return (
          <WorkspaceTitleAndDescription
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 8:
        return (
          <WorkspaceImages
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 9:
        return (
          <WorkspaceAvailabilities
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 10:
        return (
          <WorkspaceBookingDetails
            formData={formData}
            setFormData={setFormData}
            setNextDisabled={setNextDisabled}
          />
        );
      case 11:
        return (
          <WorkspaceSummary
            formData={formData}
            //onSubmit={handleFormSubmit}
          />
        );
      default:
        return <div>Page not found</div>;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] overflow-hidden">
      {/* Scrollable content area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
        <div className="container mx-auto max-w-7xl pb-32">
          {renderFormPage()}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-md">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-black h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mx-auto px-10">
          <div className="flex gap-2">
            {onBack && currentPage === 1 && (
              <button
                onClick={onBack}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                ← Back to Account
              </button>
            )}
            {currentPage > 1 && (
              <button
                onClick={prevPage}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700"
              >
                Back
              </button>
            )}
          </div>

          {currentPage < totalPages ? (
            <button
              onClick={nextPage}
              className={`px-4 py-2 rounded ${
                nextDisabled
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-black text-white"
              }`}
              disabled={nextDisabled}
            >
              Continue
            </button>
          ) : (
            <button
              className="px-4 py-2 bg-green-500 text-white rounded"
              onClick={handleFormSubmit}
              type="submit"
            >
              Submit Form
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
